import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { toast } from 'sonner';
import { PlusIcon, XMarkIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NumberInput } from '../components/ui/number-input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import ErrorBoundary from '@/components/ErrorBoundary';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { cn } from '../lib/utils';
import ProductCombobox from '../components/ProductCombobox';
import AppointmentCombobox, { AppointmentOption } from '../components/AppointmentCombobox';
import ProviderCombobox from '../components/ProviderCombobox';
import { hoyLocal } from '../utils/fechaLocal';
import { calcularItemCompra, listaDesdeCostoConIva } from '../utils/purchaseItemCalc';

interface PurchaseItem {
  id?: string;
  productId?: string;
  supplyId?: string;
  itemType?: 'product' | 'supply' | 'manual' | 'tool' | 'equipment';
  description: string;
  quantity: number;
  /** Costo de LISTA del proveedor (lo que dice la factura antes del descuento) */
  listCost?: number;
  /** % de descuento del renglón */
  discountPct?: number;
  /** Costo neto efectivo = lista con descuento (va al costo del producto) */
  unitCost: number;
  subtotal?: number;
  taxRate?: number;
  taxAmount?: number;
  total: number;
  // En compras con OT: este ítem es para la OT (no suma stock) o va a stock normal
  forAppointment?: boolean;
}

interface Provider {
  id: string;
  name: string;
  fantasyName?: string;
  cuit?: string;
  address?: string;
  paymentMethod?: string | null;
}

interface Product {
  id: string;
  name: string;
  sku: string;
  costPrice: number;
  sellingPrice: number;
  currentStock: number;
  category?: string;
  isService?: boolean;
}

interface Supply {
  id: string;
  name: string;
  costPrice?: number;
}

interface Appointment {
  id: string;
  appointmentCode?: string;
  scheduledDate?: string;
  clientName?: string;
  vehiclePlate?: string;
  serviceType?: string;
  status?: string;
}

// DatePicker (mismo que en Purchases)
const DatePickerCalendar = ({ value, onChange, id }: { value?: string; onChange: (date: string) => void; id?: string }) => {
  const [open, setOpen] = useState(false);

  const dateValue = value ? (() => {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  })() : undefined;

  const handleSelect = (date: Date | undefined) => {
    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      onChange(`${year}-${month}-${day}`);
      setOpen(false);
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          id={id}
          className={cn(
            "w-full justify-between font-normal px-4 py-3 rounded-xl border border-input bg-background h-auto",
            !dateValue && "text-muted-foreground"
          )}
        >
          {dateValue ? dateValue.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Seleccionar fecha'}
          <ChevronDownIcon className="ml-2 h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto overflow-hidden p-0" align="start">
        <Calendar
          mode="single"
          selected={dateValue}
          onSelect={handleSelect}
          captionLayout="dropdown"
          fromYear={currentYear - 1}
          toYear={currentYear + 1}
          disabled={(date) => date > new Date() || date < new Date('1900-01-01')}
        />
      </PopoverContent>
    </Popover>
  );
};

const PurchaseNew: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Modo edición: /purchases/:id/edit (solo compras aún no recepcionadas)
  const { id: editId } = useParams<{ id: string }>();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [supplies, setSupplies] = useState<Supply[]>([]);
  const [scannedProvider, setScannedProvider] = useState<{ name: string; cuit?: string } | null>(null);
  const [creatingProvider, setCreatingProvider] = useState(false);
  const [saving, setSaving] = useState(false);
  // ¿La mercadería ya llegó? Si no, la orden queda pendiente (sin stock ni deuda)
  // y se recibe después. En alta arranca tildada (llega con la mercadería en mano);
  // en edición arranca destildada (las editables están pendientes/'sent').
  const [receivedNow, setReceivedNow] = useState(!editId);
  const [items, setItems] = useState<PurchaseItem[]>([]);
  const [searchType, setSearchType] = useState<'product' | 'supply' | 'tool' | 'equipment'>('product');
  const [assetName, setAssetName] = useState('');
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentOption | null>(null);

  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      providerId: '',
      issueDate: hoyLocal(),
      invoiceNumber: '',
      perceptions: '',
      notes: '',
      taxRate: 21,
      status: 'draft',
      paymentMethod: 'contado'
    } as any
  });

  const watchProviderId = watch('providerId');
  const watchTaxRate = watch('taxRate') || 21;

  useEffect(() => {
    axios.get('/api/providers?limit=1000').then(r => setProviders(r.data.providers || [])).catch(() => {});
    axios.get('/api/products?limit=all').then(r => setProducts(r.data.products || [])).catch(() => {});
    axios.get('/api/supplies?limit=1000').then(r => setSupplies(r.data.supplies || [])).catch(() => {});
    // OTs para vincular la compra (compras puntuales para un trabajo específico)
    axios.get('/api/appointments').then(r => {
      const list: Appointment[] = r.data.appointments || r.data || [];
      // Más recientes primero: número de OT (numérico) y, si empata/falta, fecha del turno
      const otNum = (a: Appointment) => {
        const m = String(a.appointmentCode || '').match(/(\d+)/);
        return m ? parseInt(m[1], 10) : -1;
      };
      const ts = (a: Appointment) => (a.scheduledDate ? new Date(a.scheduledDate).getTime() : 0);
      const relevant = list
        .filter(a => a.status !== 'cancelled')
        .sort((a, b) => (otNum(b) - otNum(a)) || (ts(b) - ts(a)))
        .slice(0, 100);
      setAppointments(relevant);
    }).catch(() => {});
  }, []);

  // Modo edición: cargar la compra y prellenar el formulario
  useEffect(() => {
    if (!editId) return;
    axios.get('/api/purchases').then(r => {
      const p = (r.data.purchases || []).find((x: any) => x.id === editId);
      if (!p) { toast.error('Compra no encontrada'); navigate('/purchases'); return; }
      if (p.status === 'received' || p.status === 'partial') {
        toast.error('No se puede editar una compra ya recepcionada');
        navigate('/purchases');
        return;
      }
      setValue('providerId', p.providerId);
      if (p.issueDate) setValue('issueDate', String(p.issueDate).slice(0, 10));
      setValue('paymentMethod', p.paymentMethod || 'contado');
      setValue('invoiceNumber', p.invoiceNumber || '');
      setValue('perceptions', p.perceptions ? String(p.perceptions) : '');
      setValue('notes', p.notes || '');
      if (p.appointmentId) {
        setValue('appointmentId', p.appointmentId);
        setSelectedAppointment({ id: p.appointmentId, appointmentCode: p.appointmentCode, vehiclePlate: p.appointmentPlate });
      }
      setItems((p.items || []).map((it: any) => {
        const quantity = Number(it.quantity ?? it.qty ?? 1);
        const taxRate = Number(it.taxRate ?? 21);
        const discountPct = Number(it.discountPct || 0);
        const listCost = it.listCost != null ? Number(it.listCost) : Number(it.unitCost || 0);
        const c = calcularItemCompra({ quantity, listCost, discountPct, taxRate });
        return {
          productId: it.productId || undefined,
          supplyId: it.supplyId || undefined,
          itemType: it.itemType || 'manual',
          description: it.description || it.name || '',
          quantity, listCost, discountPct, taxRate,
          unitCost: c.unitCost, subtotal: c.subtotal, taxAmount: c.taxAmount, total: c.total,
          forAppointment: Number(it.forAppointment ?? 1) === 1
        } as PurchaseItem;
      }));
    }).catch(() => { toast.error('No se pudo cargar la compra'); navigate('/purchases'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  // Prefill desde el escaneo IA (llega por location.state.scan desde /purchases)
  useEffect(() => {
    const scan = (location.state as any)?.scan;
    if (!scan) return;

    if (scan.provider?.matchedId) {
      setValue('providerId', scan.provider.matchedId);
      setScannedProvider(null);
    } else if (scan.provider?.name) {
      setScannedProvider({ name: scan.provider.name, cuit: scan.provider.cuit });
    }
    if (scan.date) setValue('issueDate', scan.date);
    if (scan.paymentCondition) setValue('paymentMethod', scan.paymentCondition);

    // N° de comprobante detectado → su campo propio (con el tipo adelante si vino)
    if (scan.invoiceNumber) {
      setValue('invoiceNumber', scan.invoiceType ? `${scan.invoiceType} ${scan.invoiceNumber}` : String(scan.invoiceNumber));
    }
    // Percepciones (IIBB etc.) detectadas en la factura
    if (Number(scan.otherTaxes) > 0) {
      setValue('perceptions', String(scan.otherTaxes));
    }

    if (scan.items && scan.items.length > 0) {
      setItems(scan.items.map((item: any) => {
        const quantity = item.qty || 1;
        const listCost = item.unitCost || 0;
        const taxRate = item.taxRate ?? 21;
        const c = calcularItemCompra({ quantity, listCost, discountPct: 0, taxRate });
        return {
          description: item.name || '',
          quantity,
          listCost,
          discountPct: 0,
          unitCost: c.unitCost,
          taxRate,
          subtotal: c.subtotal,
          taxAmount: c.taxAmount,
          total: c.total,
          itemType: item.matchedSupplyId ? 'supply' : item.matchedProductId ? 'product' : 'manual',
          productId: item.matchedProductId || undefined,
          supplyId: item.matchedSupplyId || undefined,
        } as PurchaseItem;
      }));
    }
    const firstTaxRate = scan.items?.[0]?.taxRate || 21;
    setValue('taxRate', firstTaxRate);
    // Limpiar el state para que un refresh no re-aplique
    window.history.replaceState({}, '');
  }, [location.state, setValue]);

  // Autocompletar datos del proveedor elegido
  useEffect(() => {
    if (!watchProviderId) return;
    const provider = providers.find(p => p.id === watchProviderId);
    if (provider?.paymentMethod) setValue('paymentMethod', provider.paymentMethod);
  }, [watchProviderId, providers, setValue]);

  const recalcRow = (row: PurchaseItem): PurchaseItem => {
    const taxRate = Number(row.taxRate ?? watchTaxRate ?? 0);
    // Sin lista explícita, el unitCost que llega ES la lista (alta desde catálogo)
    const listCost = Number(row.listCost ?? row.unitCost ?? 0);
    const discountPct = Number(row.discountPct ?? 0);
    const c = calcularItemCompra({ quantity: Number(row.quantity) || 0, listCost, discountPct, taxRate });
    return { ...row, taxRate, listCost, discountPct, unitCost: c.unitCost, subtotal: c.subtotal, taxAmount: c.taxAmount, total: c.total };
  };

  // Actualización funcional: varios updates seguidos no se pisan entre sí
  const updateItem = (index: number, patch: Partial<PurchaseItem>) => {
    setItems(prev => {
      const next = [...prev];
      let row = { ...next[index], ...patch };

      if (patch.itemType === 'product') row.supplyId = undefined;
      if (patch.itemType === 'supply') row.productId = undefined;
      if (patch.itemType === 'manual') { row.productId = undefined; row.supplyId = undefined; }

      if (patch.supplyId) {
        const supply = supplies.find(s => s.id === patch.supplyId);
        if (supply) {
          row.description = supply.name;
          row.unitCost = Number(supply.costPrice || 0);
        }
      }

      next[index] = recalcRow(row);
      return next;
    });
  };

  const makeRow = (partial: Partial<PurchaseItem>): PurchaseItem => {
    return recalcRow({
      description: '', quantity: 1, unitCost: 0, total: 0,
      itemType: 'manual', taxRate: watchTaxRate,
      forAppointment: true,
      ...partial
    } as PurchaseItem);
  };

  // Buscar arriba y agregar como fila (mismo flujo que Remitos)
  const addItemFromProduct = (product: { id: string; name: string }) => {
    const p = products.find(x => x.id === product.id);
    setItems(prev => [...prev, makeRow({
      itemType: 'product',
      productId: product.id,
      description: p?.name || product.name,
      // Solo el COSTO del catálogo; nunca el precio de venta como fallback (es una compra)
      unitCost: Number(p?.costPrice || 0)
    })]);
  };

  const addItemFromSupply = (supply: { id: string; name: string }) => {
    const s = supplies.find(x => x.id === supply.id);
    setItems(prev => [...prev, makeRow({
      itemType: 'supply',
      supplyId: supply.id,
      description: s?.name || supply.name,
      unitCost: Number(s?.costPrice || 0)
    })]);
  };

  const addManualItem = (query?: string) => {
    setItems(prev => [...prev, makeRow({ itemType: 'manual', description: (query || '').trim() })]);
  };

  // Crear un ítem NUEVO (no existe en catálogo) del tipo del buscador: nace como
  // producto/insumo y se da de alta en el catálogo al recibir la compra.
  const addNewItem = (query: string, type: 'product' | 'supply') => {
    setItems(prev => [...prev, makeRow({ itemType: type, description: (query || '').trim() })]);
  };

  // Herramientas y maquinas: no hay catalogo que buscar (cada unidad es unica),
  // se escribe el nombre y al recibir se da de alta en Equipos y herramientas.
  const addAssetItem = () => {
    const name = assetName.trim();
    if (!name) return;
    setItems(prev => [...prev, makeRow({ itemType: searchType === 'equipment' ? 'equipment' : 'tool', description: name })]);
    setAssetName('');
  };

  const removeItem = (index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.subtotal || 0), 0);
    const taxAmount = items.reduce((sum, item) => sum + (item.taxAmount || 0), 0);
    const total = items.reduce((sum, item) => sum + (item.total || 0), 0);
    return { subtotal, taxAmount, total };
  };

  // Percepciones: "1.234,56" (es-AR) o "1234.56" → número
  const parsePerceptions = (v: any) => {
    const raw = String(v ?? '').trim();
    if (!raw) return 0;
    return raw.includes(',')
      ? (Number(raw.replace(/\./g, '').replace(',', '.')) || 0)
      : (Number(raw) || 0);
  };

  const createScannedProvider = async () => {
    if (!scannedProvider?.name) return;
    try {
      setCreatingProvider(true);
      const payload: any = { name: scannedProvider.name.trim(), isActive: true };
      if (scannedProvider.cuit) {
        const digits = scannedProvider.cuit.replace(/\D/g, '');
        payload.taxId = digits || scannedProvider.cuit.trim();
      }
      const res = await axios.post('/api/providers', payload);
      const newId = res.data?.provider?.id;
      const list = await axios.get('/api/providers?limit=1000');
      setProviders(list.data.providers || []);
      if (newId) setValue('providerId', newId);
      setScannedProvider(null);
      toast.success('Proveedor creado y seleccionado');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo crear el proveedor');
    } finally {
      setCreatingProvider(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      const validItems = items.filter(item => item.description && item.quantity > 0);
      if (validItems.length === 0) {
        toast.error('Debes agregar al menos un item con descripción');
        return;
      }
      if (!data.providerId) {
        toast.error('Seleccioná un proveedor');
        return;
      }

      setSaving(true);
      const { subtotal, taxAmount, total } = calculateTotals();
      const payload = {
        ...data,
        perceptions: parsePerceptions(data.perceptions),
        appointmentId: data.appointmentId || null,
        items: validItems.map(item => ({
          productId: item.productId,
          supplyId: item.supplyId,
          itemType: item.itemType,
          description: item.description,
          quantity: item.quantity,
          listCost: item.listCost ?? item.unitCost,
          discountPct: item.discountPct ?? 0,
          unitCost: item.unitCost,
          forAppointment: item.forAppointment !== false,
          subtotal: item.subtotal ?? item.quantity * item.unitCost,
          taxRate: item.taxRate ?? watchTaxRate,
          taxAmount: item.taxAmount ?? (item.subtotal ?? item.quantity * item.unitCost) * ((item.taxRate ?? watchTaxRate) / 100),
          total: item.total ?? (item.subtotal ?? item.quantity * item.unitCost) + (item.taxAmount ?? 0),
        })),
        subtotal,
        taxAmount,
        total,
        taxRate: watchTaxRate
      };

      if (editId) {
        // Edición: guarda los ítems y, si se tildó "recibida", la recepciona
        // (suma stock + deuda con los ítems ya actualizados).
        await axios.put(`/api/purchases/${editId}`, payload);
        if (receivedNow) {
          await axios.post(`/api/purchases/${editId}/receive`, {});
          toast.success('Compra actualizada y recibida');
        } else {
          toast.success('Compra actualizada (pendiente de recepción)');
        }
      } else {
        // Si la mercadería ya llegó → nace recibida (stock + deuda). Si no →
        // nace 'sent' (pendiente): sin stock ni deuda hasta recibirla desde la lista.
        await axios.post('/api/purchases', { ...payload, status: receivedNow ? 'received' : 'sent' });
        toast.success(receivedNow ? 'Compra creada y recibida' : 'Orden creada como pendiente de recepción');
      }
      navigate('/purchases');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.response?.data?.details?.[0]?.msg || 'Error al guardar la orden de compra';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const money = (n: number) => `$${(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const totales = calculateTotals();
  const totalFinal = totales.total + parsePerceptions(watch('perceptions'));

  // Descuento global del proveedor (sobre el total, en % o en monto): se
  // prorratea a TODOS los renglones como discountPct. Asi el costo que queda
  // en stock y la deuda de cta cte salen por el mismo mecanismo que el
  // descuento por item, sin un segundo camino de calculo.
  const subtotalLista = items.reduce((s, i) => s + (Number(i.listCost ?? i.unitCost) || 0) * (Number(i.quantity) || 0), 0);
  const descuentoTotal = Math.max(0, subtotalLista - totales.subtotal);
  const descModo: 'pct' | 'monto' = (watch as any)('discountMode') === 'monto' ? 'monto' : 'pct';
  const descValor = String((watch as any)('discountValue') ?? '');
  const aplicarDescuentoGlobal = (modo: 'pct' | 'monto', valorTxt: string) => {
    // "1.234,56" (es-AR) o "15.5" / "15,5": con coma, los puntos son miles
    const s = String(valorTxt).trim();
    const v = s.includes(',') ? Number(s.replace(/\./g, '').replace(',', '.')) : Number(s);
    if (!Number.isFinite(v) || v < 0) return;
    const pct = modo === 'pct' ? v : (subtotalLista > 0 ? (v / subtotalLista) * 100 : 0);
    // 4 decimales: en modo monto el % prorrateado tiene que devolver el monto al centavo
    const pctClamp = Math.min(100, Math.max(0, Math.round(pct * 10000) / 10000));
    setItems(prev => {
      let next = prev.map(row => recalcRow({ ...row, discountPct: pctClamp }));
      if (modo === 'monto' && subtotalLista > 0 && v <= subtotalLista && next.length) {
        // El costo unitario se redondea a centavos por renglón: con cantidades
        // > 1 la suma puede no cerrar en el monto exacto. El resto (un centavo)
        // se absorbe en el renglón de mayor subtotal para que el total quede
        // clavado en lo que se tipeó (es lo que va a la cta cte).
        const objetivo = Math.round((subtotalLista - v) * 100) / 100;
        const suma = next.reduce((s, r) => s + (Number(r.subtotal) || 0), 0);
        const residual = Math.round((objetivo - suma) * 100) / 100;
        if (residual !== 0) {
          const i = next.reduce((best, r, idx) => ((Number(r.subtotal) || 0) > (Number(next[best].subtotal) || 0) ? idx : best), 0);
          const r = next[i];
          const qty = Number(r.quantity) || 1;
          const subtotal = Math.round(((Number(r.subtotal) || 0) + residual) * 100) / 100;
          const unitCost = Math.round((subtotal / qty) * 10000) / 10000;
          const taxRate = Number(r.taxRate || 0);
          const taxAmount = Math.round(subtotal * (taxRate / 100) * 100) / 100;
          const listCost = Number(r.listCost ?? r.unitCost) || 0;
          const discountPct = listCost > 0 ? Math.round((1 - unitCost / listCost) * 1000000) / 10000 : r.discountPct;
          next = next.map((x, idx) => (idx === i
            ? { ...x, unitCost, subtotal, taxAmount, total: Math.round((subtotal + taxAmount) * 100) / 100, discountPct }
            : x));
        }
      }
      return next;
    });
  };
  const inputCls = 'px-2 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-background border-border text-foreground';

  return (
    <ErrorBoundary>
      <div className="mx-auto w-full max-w-7xl p-4 md:p-6 space-y-6">
        {/* Encabezado de página: título + acciones siempre a mano */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="icon" onClick={() => navigate('/purchases')} className="h-9 w-9 rounded-lg">
              <ArrowLeftIcon className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">{editId ? 'Editar orden de compra' : 'Nueva orden de compra'}</h1>
              <p className="text-sm text-muted-foreground">
                {editId ? 'Los cambios reemplazan los ítems de la orden.' : 'Cargá la factura del proveedor renglón por renglón.'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => navigate('/purchases')} className="h-9 rounded-lg px-4">
              Cancelar
            </Button>
            <Button type="submit" form="purchase-form" disabled={saving} className="h-9 rounded-lg px-4">
              {saving ? 'Guardando...' : (editId ? 'Guardar cambios' : 'Crear compra')}
            </Button>
          </div>
        </div>

        <form id="purchase-form" onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px] items-start">
          {/* ── Columna principal ─────────────────────────────────────── */}
          <div className="min-w-0 space-y-6">
            {/* Datos de la compra */}
            <Card className="rounded-2xl">
              <CardContent className="p-5 md:p-6 space-y-5">
                <h2 className="text-base font-semibold">Datos de la compra</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="providerId" className="font-medium text-sm">Proveedor *</Label>
                    <ProviderCombobox
                      providers={providers}
                      selectedProvider={providers.find((p) => p.id === watchProviderId) || null}
                      onSelect={(p) => setValue('providerId', p?.id || '', { shouldValidate: true })}
                      placeholder="Buscar proveedor por nombre..."
                    />
                    {errors.providerId && <p className="text-sm text-red-600 mt-1">{errors.providerId.message?.toString()}</p>}
                    {scannedProvider && !watchProviderId && (
                      <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700/60 dark:bg-amber-950/40 p-3 text-sm">
                        <p className="text-amber-800 dark:text-amber-300 font-medium">Proveedor no encontrado en tu base</p>
                        <p className="mt-0.5 text-foreground">
                          {scannedProvider.name}
                          {scannedProvider.cuit ? ` — CUIT ${scannedProvider.cuit}` : ''}
                        </p>
                        <Button type="button" size="sm" onClick={createScannedProvider} disabled={creatingProvider} className="mt-2 h-8">
                          {creatingProvider ? 'Creando...' : 'Crear proveedor con estos datos'}
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="issueDate" className="font-medium text-sm">Fecha *</Label>
                    <DatePickerCalendar
                      id="issueDate"
                      value={watch('issueDate') || hoyLocal()}
                      onChange={(date) => setValue('issueDate', date)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="invoiceNumber" className="font-medium text-sm">N° de comprobante del proveedor</Label>
                    <Input id="invoiceNumber" className="h-10 rounded-xl" placeholder="Ej: FACA 0002-00023969" {...register('invoiceNumber')} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="paymentMethod" className="font-medium text-sm">Forma de pago</Label>
                    <Select onValueChange={(value) => setValue('paymentMethod', value)} value={watch('paymentMethod') || 'contado'}>
                      <SelectTrigger className="h-10 rounded-xl">
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="contado">Contado</SelectItem>
                        <SelectItem value="transferencia">Transferencia</SelectItem>
                        <SelectItem value="cheque">Cheque</SelectItem>
                        <SelectItem value="cta_cte">Cuenta corriente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="appointmentId" className="font-medium text-sm">OT relacionada (opcional)</Label>
                    <AppointmentCombobox
                      appointments={appointments}
                      selected={selectedAppointment}
                      onSelect={(apt) => {
                        setSelectedAppointment(apt);
                        setValue('appointmentId', apt?.id || '');
                      }}
                    />
                  </div>
                </div>
                {selectedAppointment && (
                  <p className="text-xs text-muted-foreground">
                    Con OT: cada ítem tiene un tilde "→ OT" — los tildados se cargan a la OT (a precio de venta) y NO suman stock;
                    los destildados van a stock normal. Si de una compra usás solo parte en la OT, cargá el resto en otro renglón destildado.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Ítems */}
            <Card className="rounded-2xl">
              <CardContent className="p-5 md:p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-semibold">Ítems</h2>
                  {items.length > 0 && <span className="text-sm text-muted-foreground">{items.length} {items.length === 1 ? 'renglón' : 'renglones'}</span>}
                </div>

                <div className="flex flex-col gap-2 sm:flex-row">
                  <Select value={searchType} onValueChange={(v) => setSearchType(v as 'product' | 'supply' | 'tool' | 'equipment')}>
                    <SelectTrigger className="h-10 rounded-xl sm:w-40 shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="product">Producto</SelectItem>
                      <SelectItem value="supply">Insumo</SelectItem>
                      <SelectItem value="tool">Herramienta</SelectItem>
                      <SelectItem value="equipment">Máquina / Equipo</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex-1 min-w-0">
                    {(searchType === 'tool' || searchType === 'equipment') ? (
                      <div className="flex gap-2">
                        <Input
                          value={assetName}
                          onChange={(e) => setAssetName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAssetItem(); } }}
                          placeholder={searchType === 'tool' ? 'Nombre de la herramienta...' : 'Nombre de la máquina o equipo...'}
                          className="h-10 rounded-xl"
                        />
                        <Button type="button" onClick={addAssetItem} size="sm" variant="outline" className="h-10 shrink-0" disabled={!assetName.trim()}>
                          <PlusIcon className="h-4 w-4 mr-1" /> Agregar
                        </Button>
                      </div>
                    ) : searchType === 'product' ? (
                      <ProductCombobox
                        products={products.map(p => ({
                          id: p.id, name: p.name, sellingPrice: p.costPrice || 0,
                          category: p.category ?? '', isService: p.isService ?? false, sku: p.sku, stock: p.currentStock
                        }))}
                        selectedProduct={null}
                        onSelect={(product) => addItemFromProduct(product)}
                        onCreateRequested={(query) => addNewItem(query, 'product')}
                        createLabelOverride={(q) => `Crear producto nuevo "${q}"`}
                        placeholder="Buscar producto o servicio..."
                      />
                    ) : (
                      <ProductCombobox
                        products={supplies.map(s => ({
                          id: s.id, name: s.name, sellingPrice: Number(s.costPrice || 0),
                          category: 'insumo', isService: false, sku: '', stock: undefined as any
                        }))}
                        selectedProduct={null}
                        onSelect={(supply) => addItemFromSupply(supply)}
                        onCreateRequested={(query) => addNewItem(query, 'supply')}
                        createLabelOverride={(q) => `Crear insumo nuevo "${q}"`}
                        placeholder="Buscar insumo..."
                      />
                    )}
                  </div>
                  <Button type="button" onClick={() => addManualItem()} size="sm" variant="outline" className="h-10 shrink-0">
                    <PlusIcon className="h-4 w-4 mr-1" /> Ítem libre
                  </Button>
                </div>

                {items.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-muted-foreground/30 p-8 text-center text-sm text-muted-foreground">
                    Buscá un producto o insumo arriba para agregarlo a la orden.
                  </div>
                ) : (
                  // Renglones: grilla tipo tabla en contenedores anchos (encabezado y
                  // renglones comparten las columnas, así los títulos quedan sobre su
                  // campo) y tarjeta con etiquetas por campo en angostos (sidebar
                  // abierto, notebook). Las clases pq-* están en index.css.
                  <div className="rounded-xl border overflow-hidden cq-table">
                    <div className={cn('pq-head pq-cols bg-muted/60 px-3 py-2.5 text-xs font-semibold text-muted-foreground', selectedAppointment && 'pq-with-ot')}>
                      {selectedAppointment && <span className="text-center">→ OT</span>}
                      <span>Item</span>
                      <span className="text-center">Cant.</span>
                      <span className="text-right">Costo lista</span>
                      <span className="text-center">Desc.</span>
                      <span className="text-right">Neto</span>
                      <span className="text-center">IVA</span>
                      <span className="text-right">c/IVA</span>
                      <span className="text-right">Total</span>
                      <span />
                    </div>
                    <div className="bg-card">
                      {items.map((item, index) => {
                        const rate = Number(item.taxRate ?? watchTaxRate) || 0;
                        const desc = Number(item.discountPct || 0);
                        const gross = Math.round((Number(item.unitCost) || 0) * (1 + rate / 100) * 100) / 100;
                        return (
                          <div
                            key={`${item.productId || item.supplyId || 'manual'}-${index}`}
                            className={cn('pq-row pq-cols border-t border-border px-3 py-3 first:border-t-0', selectedAppointment && 'pq-with-ot')}
                          >
                            <div className="pq-main">
                              {selectedAppointment && (
                                <div className="pq-cell-ot text-center">
                                  <input
                                    type="checkbox"
                                    checked={item.forAppointment !== false}
                                    onChange={(e) => updateItem(index, { forAppointment: e.target.checked })}
                                    className="h-4 w-4 accent-cyan-600 cursor-pointer"
                                  />
                                </div>
                              )}
                              <div className="pq-cell-item min-w-0">
                                <input
                                  type="text"
                                  value={item.description}
                                  onChange={(e) => updateItem(index, { description: e.target.value })}
                                  className={`w-full h-9 ${inputCls}`}
                                  placeholder="Nombre del artículo"
                                />
                                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                  <select
                                    value={item.itemType || 'manual'}
                                    onChange={(e) => updateItem(index, { itemType: e.target.value as PurchaseItem['itemType'] })}
                                    className="text-xs border rounded-md px-1.5 py-0.5 bg-background border-border text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                                  >
                                    <option value="manual">Ítem libre (no toca stock)</option>
                                    <option value="supply">Insumo</option>
                                    <option value="product">Producto</option>
                                    <option value="tool">Herramienta</option>
                                    <option value="equipment">Máquina / Equipo</option>
                                  </select>
                                  {(item.itemType === 'supply' || item.itemType === 'product') && !item.supplyId && !item.productId && (
                                    <span className="text-xs text-primary whitespace-nowrap">nuevo · se crea al recibir</span>
                                  )}
                                  {(item.itemType === 'tool' || item.itemType === 'equipment') && (
                                    <span className="text-xs text-primary whitespace-nowrap">alta en Equipos y herramientas al recibir</span>
                                  )}
                                </div>
                              </div>
                              <div className="pq-cell-x text-center">
                                <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(index)} className="h-9 w-8 text-red-500 hover:text-red-600">
                                  <XMarkIcon className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            <div className="pq-nums">
                              <div className="pq-cell pq-cell-qty">
                                <span className="pq-label">Cant.</span>
                                <NumberInput min={1} value={item.quantity} onChange={(v) => updateItem(index, { quantity: v })} className={`w-full h-9 text-center ${inputCls}`} />
                              </div>
                              <div className="pq-cell pq-cell-lista">
                                <span className="pq-label">Costo lista</span>
                                <NumberInput min={0} step="0.01" value={item.listCost ?? item.unitCost} onChange={(v) => updateItem(index, { listCost: v })} className={`w-full h-9 text-right ${inputCls}`} />
                              </div>
                              <div className="pq-cell pq-cell-desc">
                                <span className="pq-label">Desc.</span>
                                {/* Descuento del renglón: la lista queda, el neto baja */}
                                <div className="relative">
                                  <NumberInput min={0} max={100} step="0.5" value={desc} onChange={(v) => updateItem(index, { discountPct: v })} className={`w-full h-9 pr-6 text-right ${inputCls}`} />
                                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                                </div>
                              </div>
                              <div className="pq-cell pq-cell-neto text-right">
                                <span className="pq-label">Neto</span>
                                <span className={cn('block py-2 text-sm leading-5 tabular-nums', desc > 0 ? 'font-medium' : 'text-muted-foreground')}>{money(item.unitCost)}</span>
                              </div>
                              <div className="pq-cell pq-cell-iva">
                                <span className="pq-label">IVA</span>
                                <Select value={String(item.taxRate ?? watchTaxRate)} onValueChange={(value) => updateItem(index, { taxRate: Number(value) })}>
                                  <SelectTrigger className="h-9 rounded-lg w-full text-sm">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="0">Sin IVA</SelectItem>
                                    <SelectItem value="10.5">10.5%</SelectItem>
                                    <SelectItem value="21">21%</SelectItem>
                                    <SelectItem value="27">27%</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="pq-cell pq-cell-civa">
                                <span className="pq-label">c/IVA</span>
                                {/* c/IVA editable: si la factura trae el precio con IVA, se escribe acá y la lista se calcula hacia atrás */}
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={gross}
                                  onChange={(e) => updateItem(index, { listCost: listaDesdeCostoConIva({ grossUnit: Number(e.target.value) || 0, discountPct: desc, taxRate: rate }) })}
                                  className={`w-full h-9 text-right ${inputCls}`}
                                />
                              </div>
                              <div className="pq-cell pq-cell-total text-right">
                                <span className="pq-label">Total</span>
                                <span className="block py-2 text-sm leading-5 font-semibold whitespace-nowrap tabular-nums">{money(item.total || 0)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notas */}
            <Card className="rounded-2xl">
              <CardContent className="p-5 md:p-6 space-y-2">
                <Label htmlFor="notes" className="text-base font-semibold">Notas</Label>
                <Textarea id="notes" {...register('notes')} rows={3} className="px-4 py-3 rounded-xl border border-input bg-background resize-none" placeholder="Notas adicionales..." />
              </CardContent>
            </Card>
          </div>

          {/* ── Resumen (fijo al scrollear en pantallas grandes) ──────── */}
          <div className="lg:sticky lg:top-6 space-y-4">
            <Card className="rounded-2xl">
              <CardContent className="p-5 space-y-3">
                <h2 className="text-base font-semibold">Resumen</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Subtotal lista</span><span className="tabular-nums">{money(subtotalLista)}</span></div>
                  {/* Descuento global: % o $, se prorratea a todos los renglones */}
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">Descuento</span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => { (setValue as any)('discountMode', 'pct'); if (descValor) aplicarDescuentoGlobal('pct', descValor); }}
                        className={`h-8 rounded-md border px-2 text-xs font-semibold ${descModo === 'pct' ? 'bg-foreground text-background' : 'text-muted-foreground'}`}
                      >
                        %
                      </button>
                      <button
                        type="button"
                        onClick={() => { (setValue as any)('discountMode', 'monto'); if (descValor) aplicarDescuentoGlobal('monto', descValor); }}
                        className={`h-8 rounded-md border px-2 text-xs font-semibold ${descModo === 'monto' ? 'bg-foreground text-background' : 'text-muted-foreground'}`}
                      >
                        $
                      </button>
                      <Input
                        inputMode="decimal"
                        placeholder={descModo === 'pct' ? '0' : '0,00'}
                        value={descValor}
                        onChange={(e) => { (setValue as any)('discountValue', e.target.value); aplicarDescuentoGlobal(descModo, e.target.value); }}
                        className="h-8 w-24 text-right"
                      />
                    </div>
                  </div>
                  {descuentoTotal > 0 && (
                    <div className="flex justify-between text-emerald-600 dark:text-emerald-400"><span>Descuento aplicado</span><span className="tabular-nums">-{money(descuentoTotal)}</span></div>
                  )}
                  <div className="flex justify-between"><span className="text-muted-foreground">Subtotal neto</span><span className="tabular-nums">{money(totales.subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">IVA</span><span className="tabular-nums">{money(totales.taxAmount)}</span></div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">Percepciones</span>
                    <div className="flex items-center gap-1">
                      <span className="text-muted-foreground">$</span>
                      <Input inputMode="decimal" placeholder="0,00" {...register('perceptions')} className="h-8 w-28 text-right" />
                    </div>
                  </div>
                </div>
                <div className="flex items-baseline justify-between border-t pt-3">
                  <span className="font-semibold">Total</span>
                  <span className="text-2xl font-bold tabular-nums">{money(totalFinal)}</span>
                </div>
                {items.length > 0 && descuentoTotal > 0 && (
                  <p className="text-xs text-muted-foreground">
                    El descuento queda repartido en cada renglón: el costo neto de cada producto ya lo incluye.
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="rounded-2xl">
              <CardContent className="p-5">
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input type="checkbox" checked={receivedNow} onChange={(e) => setReceivedNow(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-input" />
                  <span className="text-sm">
                    <span className="font-medium">Mercadería recibida</span>
                    <span className="block text-xs text-muted-foreground font-normal">
                      {receivedNow
                        ? (editId ? 'Al guardar suma stock y registra la deuda del proveedor.' : 'Al crearla suma stock y registra la deuda del proveedor.')
                        : 'Queda pendiente: no toca stock ni deuda. La recibís después desde la lista.'}
                    </span>
                  </span>
                </label>
              </CardContent>
            </Card>

            <Button type="submit" disabled={saving} className="w-full h-10 rounded-lg">
              {saving ? 'Guardando...' : (editId ? 'Guardar cambios' : 'Crear compra')}
            </Button>
          </div>
        </form>
      </div>
    </ErrorBoundary>
  );
};

export default PurchaseNew;
